import { normalizeSlotLabel } from "./slotLabel.js";

export const VARIANT_CONFIG_TAG_PREFIX = "variant_cfg:";

export type VariantTransformRule = {
  slots: string[];
  fromNominal: string;
  toNominal: string;
};

/** Additive BOM lines for a delta named-variant default effect. */
export type VariantDeltaLine = {
  material_id: string | null;
  /** Present when a slot-scale delta was materialized from a part-use parent line. */
  material_use_id?: string | null;
  material_code: string;
  material_name: string;
  quantity: number;
  slot_label: string;
};

/** Catalog estimate: new total ≈ parent slot qty × scale_factor; add the difference. */
export type VariantSlotScaleRule = {
  slot_label: string;
  scale_factor: number;
};

/**
 * Catalog estimate: add a different material with qty = round(Σ source-slot qty × factor).
 * Materialized at apply into absolute delta_lines (same pipeline as slot scale).
 */
export type VariantProportionalAddRule = {
  /** Parent slot whose qty drives the formula. */
  source_slot_label: string;
  /** Full multiplier (NOT factor − 1). */
  quantity_factor: number;
  material_id: string | null;
  material_code: string;
  material_name: string;
  material_use_id?: string | null;
  /** Destination slot; empty/omitted → source_slot_label. */
  slot_label?: string;
};

export type VariantConfig =
  | {
      mode: "delta";
      delta_lines?: VariantDeltaLine[];
      slot_scale_rules?: VariantSlotScaleRule[];
      proportional_add_rules?: VariantProportionalAddRule[];
    }
  | { mode: "transform"; rules: VariantTransformRule[] }
  | {
      mode: "hybrid";
      rules: VariantTransformRule[];
      delta_lines?: VariantDeltaLine[];
      slot_scale_rules?: VariantSlotScaleRule[];
      proportional_add_rules?: VariantProportionalAddRule[];
    };

export const DEFAULT_VARIANT_CONFIG: VariantConfig = { mode: "delta" };

function normalizeRule(raw: unknown): VariantTransformRule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const slotsRaw = r.slots;
  const slots = Array.isArray(slotsRaw)
    ? slotsRaw
        .map((s) => (typeof s === "string" ? normalizeSlotLabel(s) : ""))
        .filter((s) => s.length > 0)
    : [];
  const fromNominal =
    typeof r.fromNominal === "string"
      ? r.fromNominal.trim().toLowerCase()
      : typeof r.from === "string"
        ? r.from.trim().toLowerCase()
        : "";
  const toNominal =
    typeof r.toNominal === "string"
      ? r.toNominal.trim().toLowerCase()
      : typeof r.to === "string"
        ? r.to.trim().toLowerCase()
        : "";
  if (!fromNominal || !toNominal || slots.length === 0) return null;
  return { slots, fromNominal, toNominal };
}

function normalizeDeltaLine(raw: unknown): VariantDeltaLine | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const quantity = Number(r.quantity);
  if (!Number.isFinite(quantity) || quantity === 0) return null;
  const material_id =
    typeof r.material_id === "string" && r.material_id.trim() ? r.material_id.trim() : null;
  const material_use_id =
    typeof r.material_use_id === "string" && r.material_use_id.trim()
      ? r.material_use_id.trim()
      : null;
  const material_code = typeof r.material_code === "string" ? r.material_code.trim() : "";
  const material_name = typeof r.material_name === "string" ? r.material_name.trim() : "";
  if (!material_id && !material_use_id && !material_code && !material_name) return null;
  return {
    material_id,
    ...(material_use_id ? { material_use_id } : {}),
    material_code,
    material_name,
    quantity,
    slot_label:
      typeof r.slot_label === "string" ? normalizeSlotLabel(r.slot_label) : "",
  };
}

function normalizeSlotScaleRule(raw: unknown): VariantSlotScaleRule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const slot_label =
    typeof r.slot_label === "string" ? normalizeSlotLabel(r.slot_label) : "";
  const scale_factor = Number(r.scale_factor);
  if (!slot_label || !Number.isFinite(scale_factor) || scale_factor <= 0) return null;
  return { slot_label, scale_factor };
}

function normalizeProportionalAddRule(raw: unknown): VariantProportionalAddRule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const source_slot_label =
    typeof r.source_slot_label === "string" ? normalizeSlotLabel(r.source_slot_label) : "";
  const quantity_factor = Number(r.quantity_factor);
  if (!source_slot_label || !Number.isFinite(quantity_factor) || quantity_factor <= 0) return null;
  const material_id =
    typeof r.material_id === "string" && r.material_id.trim() ? r.material_id.trim() : null;
  const material_use_id =
    typeof r.material_use_id === "string" && r.material_use_id.trim()
      ? r.material_use_id.trim()
      : null;
  const material_code = typeof r.material_code === "string" ? r.material_code.trim() : "";
  const material_name = typeof r.material_name === "string" ? r.material_name.trim() : "";
  if (!material_id && !material_use_id && !material_code && !material_name) return null;
  const slot_label =
    typeof r.slot_label === "string" && r.slot_label.trim()
      ? normalizeSlotLabel(r.slot_label)
      : undefined;
  return {
    source_slot_label,
    quantity_factor,
    material_id,
    ...(material_use_id ? { material_use_id } : {}),
    material_code,
    material_name,
    ...(slot_label ? { slot_label } : {}),
  };
}

function normalizeDeltaFields(r: Record<string, unknown>): {
  delta_lines: VariantDeltaLine[];
  slot_scale_rules: VariantSlotScaleRule[];
  proportional_add_rules: VariantProportionalAddRule[];
} {
  const deltaRaw = Array.isArray(r.delta_lines) ? r.delta_lines : [];
  const delta_lines = deltaRaw
    .map(normalizeDeltaLine)
    .filter((ln): ln is VariantDeltaLine => ln != null);
  const scaleRaw = Array.isArray(r.slot_scale_rules) ? r.slot_scale_rules : [];
  const slot_scale_rules = scaleRaw
    .map(normalizeSlotScaleRule)
    .filter((rule): rule is VariantSlotScaleRule => rule != null);
  const propRaw = Array.isArray(r.proportional_add_rules) ? r.proportional_add_rules : [];
  const proportional_add_rules = propRaw
    .map(normalizeProportionalAddRule)
    .filter((rule): rule is VariantProportionalAddRule => rule != null);
  return { delta_lines, slot_scale_rules, proportional_add_rules };
}

function deltaConfigFromFields(
  delta_lines: VariantDeltaLine[],
  slot_scale_rules: VariantSlotScaleRule[],
  proportional_add_rules: VariantProportionalAddRule[],
): Extract<VariantConfig, { mode: "delta" }> {
  const out: Extract<VariantConfig, { mode: "delta" }> = { mode: "delta" };
  if (delta_lines.length > 0) out.delta_lines = delta_lines;
  if (slot_scale_rules.length > 0) out.slot_scale_rules = slot_scale_rules;
  if (proportional_add_rules.length > 0) out.proportional_add_rules = proportional_add_rules;
  return out;
}

function hasDeltaContent(
  delta_lines: VariantDeltaLine[],
  slot_scale_rules: VariantSlotScaleRule[],
  proportional_add_rules: VariantProportionalAddRule[],
): boolean {
  return (
    delta_lines.length > 0 ||
    slot_scale_rules.length > 0 ||
    proportional_add_rules.length > 0
  );
}

export function normalizeVariantConfig(raw: unknown): VariantConfig {
  if (!raw || typeof raw !== "object") return DEFAULT_VARIANT_CONFIG;
  const r = raw as Record<string, unknown>;

  if (r.mode === "transform") {
    const rulesRaw = Array.isArray(r.rules) ? r.rules : [];
    const rules = rulesRaw
      .map(normalizeRule)
      .filter((rule): rule is VariantTransformRule => rule != null);
    if (rules.length === 0) return DEFAULT_VARIANT_CONFIG;
    return { mode: "transform", rules };
  }

  if (r.mode === "hybrid") {
    const rulesRaw = Array.isArray(r.rules) ? r.rules : [];
    const rules = rulesRaw
      .map(normalizeRule)
      .filter((rule): rule is VariantTransformRule => rule != null);
    const { delta_lines, slot_scale_rules, proportional_add_rules } = normalizeDeltaFields(r);
    const hasDelta = hasDeltaContent(delta_lines, slot_scale_rules, proportional_add_rules);
    if (rules.length === 0 && !hasDelta) return DEFAULT_VARIANT_CONFIG;
    if (rules.length === 0) {
      return deltaConfigFromFields(delta_lines, slot_scale_rules, proportional_add_rules);
    }
    if (!hasDelta) return { mode: "transform", rules };
    const out: Extract<VariantConfig, { mode: "hybrid" }> = { mode: "hybrid", rules };
    if (delta_lines.length > 0) out.delta_lines = delta_lines;
    if (slot_scale_rules.length > 0) out.slot_scale_rules = slot_scale_rules;
    if (proportional_add_rules.length > 0) out.proportional_add_rules = proportional_add_rules;
    return out;
  }

  const { delta_lines, slot_scale_rules, proportional_add_rules } = normalizeDeltaFields(r);
  return deltaConfigFromFields(delta_lines, slot_scale_rules, proportional_add_rules);
}

export function parseVariantConfigFromTags(tags: readonly string[]): VariantConfig {
  const cfgTag = tags.find((t) => t.startsWith(VARIANT_CONFIG_TAG_PREFIX));
  if (!cfgTag) return DEFAULT_VARIANT_CONFIG;
  try {
    return normalizeVariantConfig(JSON.parse(cfgTag.slice(VARIANT_CONFIG_TAG_PREFIX.length)));
  } catch {
    return DEFAULT_VARIANT_CONFIG;
  }
}

export function mergeVariantConfigIntoTags(tags: readonly string[], config: VariantConfig): string[] {
  const without = tags.filter((t) => !t.startsWith(VARIANT_CONFIG_TAG_PREFIX));
  if (config.mode === "delta") {
    const hasLines = (config.delta_lines?.length ?? 0) > 0;
    const hasScale = (config.slot_scale_rules?.length ?? 0) > 0;
    const hasProp = (config.proportional_add_rules?.length ?? 0) > 0;
    if (!hasLines && !hasScale && !hasProp) return without;
  }
  return [...without, `${VARIANT_CONFIG_TAG_PREFIX}${JSON.stringify(config)}`];
}

import { isImpliedVariantTag } from "./impliedVariantTag.js";

/** Tags suitable for display in recipe meta forms (hides machine config and imply rules). */
export function visibleRecipeTags(tags: readonly string[]): string[] {
  return tags.filter((t) => !t.startsWith(VARIANT_CONFIG_TAG_PREFIX) && !isImpliedVariantTag(t));
}

/** True when config carries transform rules (pure transform or hybrid). */
export function isTransformVariant(
  config: VariantConfig,
): config is Extract<VariantConfig, { mode: "transform" | "hybrid" }> {
  return config.mode === "transform" || config.mode === "hybrid";
}

export function variantConfigHasDeltaSide(config: VariantConfig): boolean {
  if (config.mode === "transform") return false;
  return (
    (config.delta_lines?.length ?? 0) > 0 ||
    (config.slot_scale_rules?.length ?? 0) > 0 ||
    (config.proportional_add_rules?.length ?? 0) > 0
  );
}
